import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface LmGeneratorItemState {
  /** The currently selected source/book id. */
  activeSourceId: string;
  /** The final generated words (persisted; shown as final on revisit). */
  words: string[];
  /**
   * Whether the learner has produced output at least once. This is the
   * activity-completion flag (read by the notebook). It is "sticky": once
   * earned it survives clearing the output or switching source, because
   * completion here means "engaged/produced output", not "an answer is
   * currently present" (unlike OEQ).
   */
  complete: boolean;
}

export type LmGeneratorSliceState = Record<string, LmGeneratorItemState>;

const getDefaultState = (): LmGeneratorItemState => ({
  activeSourceId: "",
  words: [],
  complete: false,
});

const lmGeneratorSlice = createSlice({
  name: "lmGenerator",
  initialState: {} as LmGeneratorSliceState,
  reducers: {
    setSource: (
      state,
      action: PayloadAction<{ componentID: string; sourceId: string }>
    ) => {
      const { componentID, sourceId } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].activeSourceId = sourceId;
      // Switching source clears the persisted output but keeps `complete`.
      state[componentID].words = [];
    },
    setOutput: (
      state,
      action: PayloadAction<{ componentID: string; words: string[] }>
    ) => {
      const { componentID, words } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].words = words;
      // First output produced ⇒ the activity counts as complete.
      state[componentID].complete = true;
    },
    clearOutput: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      // Clear the visible/persisted output but keep `complete` (sticky).
      state[componentID].words = [];
    },
    reset: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      state[componentID] = getDefaultState();
    },
  },
});

export const lmGeneratorActions = lmGeneratorSlice.actions;
export const lmGeneratorReducer = lmGeneratorSlice.reducer;

export const selectLmGeneratorState = (
  state: { lmGenerator?: LmGeneratorSliceState },
  componentId: string
) => state.lmGenerator?.[componentId] || getDefaultState();
